import type { ChatDetail, ChatParticipant, ChatThread } from '../model/types';

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function isSystemManagedGroupContext(context: Record<string, unknown>): boolean {
  return !!(
    context.module ||
    context.intake ||
    context.course ||
    context.group ||
    context.moduleTitle ||
    context.intakeId
  );
}

export function getMyParticipant(
  detail: ChatDetail | null,
  currentUserId: number,
): ChatParticipant | null {
  if (!detail) return null;
  return detail.participants.find((participant) => participant.user.id === currentUserId) ?? null;
}

export function isStaffCreatedUserGroupContext(context: Record<string, unknown>): boolean {
  if (isSystemManagedGroupContext(context)) {
    return false;
  }

  return readString(context.origin)?.toLowerCase() === 'user';
}

function isStaffCreatedUserGroupDetail(detail: ChatDetail): boolean {
  if (detail.kind !== 'group') {
    return false;
  }

  return isStaffCreatedUserGroupContext(detail.context);
}

export function isGroupOwner(detail: ChatDetail | null, currentUserId: number): boolean {
  return getMyParticipant(detail, currentUserId)?.chatRole?.toLowerCase() === 'owner';
}

export function canManageStaffGroupChat(
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
): boolean {
  if (!detail || !isStaffUser || detail.kind !== 'group') {
    return false;
  }

  if (isSystemManagedGroupContext(detail.context)) {
    return false;
  }

  const origin = readString(detail.context.origin)?.toLowerCase();
  if (origin === 'system') {
    return false;
  }

  const role = getMyParticipant(detail, currentUserId)?.chatRole?.toLowerCase();
  return role === 'owner' || role === 'admin';
}

export function canDeleteStaffGroupChat(
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
): boolean {
  if (!detail || !isStaffUser) {
    return false;
  }

  return isStaffCreatedUserGroupDetail(detail) && isGroupOwner(detail, currentUserId);
}

export function canToggleStaffGroupAdmin(
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
): boolean {
  return canDeleteStaffGroupChat(detail, currentUserId, isStaffUser);
}

export function formatParticipantRole(status: string): string {
  const role = status.toLowerCase();
  if (role === 'owner') return 'Owner';
  if (role === 'admin') return 'Admin';
  return 'Member';
}

export function canManageGroupParticipants(
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
): boolean {
  if (!detail || detail.kind !== 'group') {
    return false;
  }

  if (canManageStaffGroupChat(detail, currentUserId, isStaffUser)) {
    return true;
  }

  const role = getMyParticipant(detail, currentUserId)?.chatRole?.toLowerCase();
  return role === 'owner' || role === 'admin';
}

export function canPromoteGroupParticipantToAdmin(
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
  participant: ChatParticipant,
): boolean {
  if (!canManageGroupParticipants(detail, currentUserId, isStaffUser)) {
    return false;
  }

  if (participant.user.id === currentUserId) {
    return false;
  }

  const participantRole = participant.chatRole.toLowerCase();
  if (participantRole === 'owner' || participantRole === 'admin') {
    return false;
  }

  if (!detail) {
    return false;
  }

  if (canManageStaffGroupChat(detail, currentUserId, isStaffUser)) {
    return participant.user.accountType === 'staff';
  }

  return true;
}

export function canRemoveGroupParticipant(
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
  participant: ChatParticipant,
): boolean {
  if (!canManageGroupParticipants(detail, currentUserId, isStaffUser)) {
    return false;
  }

  if (participant.user.id === currentUserId) {
    return false;
  }

  return participant.chatRole.toLowerCase() !== 'owner';
}

export function canToggleParticipantAdminRole(
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
  participant: ChatParticipant,
): boolean {
  if (!detail || !canToggleStaffGroupAdmin(detail, currentUserId, isStaffUser)) {
    return false;
  }

  if (participant.user.id === currentUserId) {
    return false;
  }

  const role = participant.chatRole.toLowerCase();
  if (role === 'owner') {
    return false;
  }

  return participant.user.accountType === 'staff';
}

export function canPinChatMessage(
  thread: ChatThread | null,
  detail: ChatDetail | null,
  currentUserId: number,
  isStaffUser: boolean,
): boolean {
  if (!thread) {
    return false;
  }

  if ((detail?.status ?? thread.status) !== 'active') {
    return false;
  }

  if (thread.kind === 'personal') {
    return true;
  }

  if (thread.kind === 'group') {
    const context = detail?.context ?? thread.context;

    if (isSystemManagedGroupContext(context)) {
      if (!detail || detail.id !== thread.id) {
        return false;
      }

      const role = getMyParticipant(detail, currentUserId)?.chatRole?.toLowerCase();
      return role === 'owner' || role === 'admin';
    }

    return true;
  }

  return false;
}
